/**
 * Shared chart utilities for consistent styling across analytics
 */

// Re-export formatters from centralized location
export { formatCurrency, formatCompactCurrency, formatPercent, formatNumber } from "../lib/formatters";

// Color palette for charts
export const CHART_COLORS = {
  primary: "hsl(var(--primary))",
  success: "#22c55e",
  warning: "#f59e0b", 
  danger: "#ef4444",
  info: "#3b82f6",
  purple: "#8b5cf6",
  pink: "#ec4899",
  teal: "#14b8a6",
  orange: "#f97316",
  cyan: "#06b6d4",
};

// Categorical color scale for pie/bar charts
export const CATEGORY_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#6366f1",
];

// Common chart styling
export const CHART_STYLE = {
  grid: { strokeDasharray: "3 3", stroke: "hsl(var(--border))" },
  axis: { stroke: "hsl(var(--muted-foreground))", fontSize: 12 },
  tooltip: { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", padding: "8px 12px" },
};

// Get color based on value (positive = green, negative = red)
export const getValueColor = (value) => {
  if (value > 0) return CHART_COLORS.success;
  if (value < 0) return CHART_COLORS.danger;
  return CHART_COLORS.warning;
};

// Get efficiency color (under budget = green, over = red)
export const getEfficiencyColor = (percent) => {
  if (percent <= 80) return CHART_COLORS.success;
  if (percent <= 100) return CHART_COLORS.warning;
  return CHART_COLORS.danger;
};

// Format month label (YYYY-MM -> Jan '24)
export const formatMonthLabel = (monthStr) => {
  if (!monthStr) return "";
  const [year, month] = monthStr.split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[parseInt(month) - 1]} '${year.slice(2)}`;
};

// Custom tooltip component styles
export const tooltipStyles = {
  contentStyle: CHART_STYLE.tooltip,
  labelStyle: { fontWeight: "bold", marginBottom: "4px" },
};

// Export data to CSV
export const exportToCSV = (data, filename, headers, fieldMapping = null) => {
  const headerRow = headers.join(",");
  
  const dataRows = data.map(row => {
    return headers.map(h => {
      // Use field mapping if provided, otherwise derive field name from header
      const fieldName = fieldMapping?.[h] || h.toLowerCase().replace(/ /g, "_").replace(/[/%]/g, "");
      const value = row[fieldName] ?? "";
      // Escape values with commas or quotes
      if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(",");
  });
  
  const csvContent = [headerRow, ...dataRows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Default row count options for tables
export const ROW_COUNT_OPTIONS = [5, 10, 15, 20, 50];
export const DEFAULT_ROW_COUNT = 10;

// Date range presets
export const DATE_RANGE_PRESETS = [
  { label: "Last 30 Days", value: "30d", days: 30 },
  { label: "Last 90 Days", value: "90d", days: 90 },
  { label: "Last 365 Days", value: "365d", days: 365 },
  { label: "Year to Date", value: "ytd", days: null },
  { label: "All Time", value: "all", days: null },
];

// Filter data by date range
export const filterByDateRange = (data, dateField, rangeValue) => {
  if (!rangeValue || rangeValue === "all") return data;
  
  const now = new Date();
  let startDate;
  
  if (rangeValue === "ytd") {
    startDate = new Date(now.getFullYear(), 0, 1);
  } else {
    const preset = DATE_RANGE_PRESETS.find(p => p.value === rangeValue);
    if (!preset?.days) return data;
    startDate = new Date(now);
    startDate.setDate(startDate.getDate() - preset.days);
  }
  
  return data.filter(item => {
    const itemDate = item[dateField];
    if (!itemDate) return true; // Include items without dates
    return new Date(itemDate) >= startDate;
  });
};

// Generate PDF content for analytics
export const generateAnalyticsPdfContent = (title, sections) => {
  const sectionHtml = sections.map(section => `
    <div class="avoid-break" style="margin-bottom: 24px;">
      <h2 style="color: #1e40af; border-bottom: 1px solid #ddd; padding-bottom: 8px;">${section.title}</h2>
      ${section.content}
    </div>
  `).join("");
  
  return `
    <h1 class="report-title">${title}</h1>
    <p class="subtitle">Generated on ${new Date().toLocaleDateString("en-US", { 
      weekday: "long", year: "numeric", month: "long", day: "numeric" 
    })}</p>
    ${sectionHtml}
    <div class="footer">
      <p>V2R Masonry - Analytics Report</p>
    </div>
  `;
};

// Generate table HTML for PDF
export const generateTableHtml = (headers, rows, alignments = []) => {
  const headerHtml = headers.map((h, i) => 
    `<th class="${alignments[i] === 'right' ? 'text-right' : ''}">${h}</th>`
  ).join("");
  
  const rowsHtml = rows.map(row => {
    const cells = row.map((cell, i) => 
      `<td class="${alignments[i] === 'right' ? 'text-right' : ''}">${cell}</td>`
    ).join("");
    return `<tr>${cells}</tr>`;
  }).join("");
  
  return `
    <table>
      <thead><tr>${headerHtml}</tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
  `;
};
